// src/redux/branding/reducer.ts
import { createReducer } from '@reduxjs/toolkit';
import { initialState, DEFAULT_NEUTRAL } from './state';
import { fetchSiteConfigAction } from './actions';

/**
 * Currency prefixes from the remote site-config are tenant-configured and are
 * often HTML entities (e.g. "R&nbsp;", "&#8358;&nbsp;", "&pound;"). We keep the
 * raw value (only length-capped) and DECODE + text-render it in CurrencyPrefix,
 * which is XSS-safe (React escapes the decoded text — no HTML sink). Do NOT
 * strip entity characters here (`&`, `#`, `;`) — that corrupts the glyph
 * (e.g. "R&nbsp;" → "Rnbsp"). The length cap fits entity-encoded prefixes.
 */
function sanitiseCurrencyPrefix(raw: string | null | undefined): string {
    if (!raw) return '';
    return String(raw).slice(0, 40);
}

export const reducer = createReducer(initialState, builder =>
    builder
        // Mark the request in flight but KEEP `loaded` as-is: on a background
        // revalidation of an already-loaded config, flipping loaded back to
        // false is what strips the brand vars from :root and flashes default
        // Swool blue (F21). loaded only becomes true once, on the first success.
        .addCase(fetchSiteConfigAction.pending, state => ({
            ...state,
            error: false,
            isFetching: true,
        }))
        .addCase(fetchSiteConfigAction.fulfilled, (state, action) => {
            const { name, logoUrl, branding, currencyPrefix } = action.payload.results;
            return {
                ...state,
                name: name ?? null,
                logoUrl: logoUrl ?? null,
                primary: branding?.primary || initialState.primary,
                secondary: branding?.secondary || DEFAULT_NEUTRAL,
                currencyPrefix: sanitiseCurrencyPrefix(currencyPrefix),
                loaded: true,
                error: false,
                isFetching: false,
            };
        })
        // On error keep whatever brand is already in state (a returning
        // visitor's cached brand, or the neutral defaults) — never revert a
        // loaded brand to defaults here (F22 is handled in the facade too).
        .addCase(fetchSiteConfigAction.rejected, state => ({
            ...state,
            loaded: true,
            error: true,
            isFetching: false,
        }))
);
